import React from 'react';
import { Check, Clock, AlertTriangle } from 'lucide-react';
import { IntegrationMilestone } from '../../types/integration';
import { formatDate } from '../../utils/formatters';

interface IntegrationTimelineProps {
  milestones: IntegrationMilestone[];
}

export default function IntegrationTimeline({ milestones }: IntegrationTimelineProps) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <Check className="w-5 h-5 text-green-500" />;
      case 'delayed':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      default:
        return <Clock className="w-5 h-5 text-blue-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-50 border-green-200';
      case 'delayed':
        return 'bg-red-50 border-red-200';
      case 'in_progress':
        return 'bg-yellow-50 border-yellow-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  const sortedMilestones = [...milestones].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  return (
    <div className="relative">
      <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200" />
      
      <div className="space-y-8">
        {sortedMilestones.map((milestone, index) => (
          <div key={milestone.id} className="relative">
            <div className="flex items-center">
              <div className="absolute left-0 mt-1">
                {getStatusIcon(milestone.status)}
              </div>
              
              <div className="ml-16">
                <div className={`p-4 rounded-lg border ${getStatusColor(milestone.status)}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-900">
                      {milestone.title}
                    </h4>
                    <span className="text-sm text-gray-500">
                      {formatDate(milestone.date)}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600">
                    {milestone.description}
                  </p>

                  {milestone.dependencies && milestone.dependencies.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500">Dependencies:</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {milestone.dependencies.map((dep, i) => (
                          <span
                            key={i}
                            className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700"
                          >
                            {dep}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {milestone.workstreamId && (
                    <div className="mt-2">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        Workstream: {milestone.workstreamId}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}